import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { Button } from '@/components/Button';
import { cn } from '@/lib/utils';
import useFetchCandidateProfileCustomFields from '@/features/agent/form/api/useFetchCandidateProfileCustomFields';
import useBulkCreateCandidateFormItems from '@/features/agent/form/api/useBulkCreateCandidateFormItems';
import useCreateCandidateFormItem from '@/features/agent/form/api/useCreateCandidateFormItem';
import { showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type {
  CandidateFormItemFormType,
  CandidateFormItemFormatType,
  SharedCustomField,
} from '@/types/serializers';

import AddItemCandidateItems from './AddItemCandidateItems';
import AddItemManual from './AddItemManual';

type Props = {
  open: boolean;
  onClose: () => void;
  excludedCustomFieldIds?: number[];
  /** 追加成功時に呼ばれる（追加後に一覧の最後の item を選択する用途で FE 側で使う） */
  onAdded?: () => void;
};

type AddMode = 'from_candidate_items' | 'manual';

export default function AddItemDialog({
  open,
  onClose,
  excludedCustomFieldIds = [],
  onAdded,
}: Props) {
  const [mode, setMode] = useState<AddMode>('from_candidate_items');
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [manualTitle, setManualTitle] = useState('');
  const [manualRequired, setManualRequired] = useState(false);
  const [manualFormType, setManualFormType] = useState<CandidateFormItemFormType>('text');
  const [manualDescription, setManualDescription] = useState('');
  const [manualPlaceholder, setManualPlaceholder] = useState('');
  const [manualSuffix, setManualSuffix] = useState('');
  const [manualFormatType, setManualFormatType] = useState<CandidateFormItemFormatType>('nothing');
  const [manualOptions, setManualOptions] = useState<string[]>([]);
  const [manualMinLength, setManualMinLength] = useState('');
  const [manualMaxLength, setManualMaxLength] = useState('');
  const { data, isLoading, isError } = useFetchCandidateProfileCustomFields();
  const { mutateAsync: bulkCreate, isPending: isCreating } = useBulkCreateCandidateFormItems();
  const { mutateAsync: createItem, isPending: isCreatingManual } = useCreateCandidateFormItem();

  useEffect(() => {
    if (!open) return;
    // モーダルを開いたタイミングで初期化（期待挙動が変わる場合は後で調整する）
    setMode('from_candidate_items');
    setSelectedIds({});
    setErrorMessage(null);
    setManualTitle('');
    setManualRequired(false);
    setManualFormType('text');
    setManualDescription('');
    setManualPlaceholder('');
    setManualSuffix('');
    setManualFormatType('nothing');
    setManualOptions([]);
    setManualMinLength('');
    setManualMaxLength('');
  }, [open]);

  const customFields = useMemo(() => {
    const records = (data?.records ?? []) as SharedCustomField[];
    return [...records].sort((a, b) => a.order_number - b.order_number);
  }, [data?.records]);
  const visibleCustomFields = useMemo(() => {
    if (excludedCustomFieldIds.length === 0) return customFields;
    const excluded = new Set(excludedCustomFieldIds);
    return customFields.filter((f) => !excluded.has(f.id));
  }, [customFields, excludedCustomFieldIds]);
  const selectedCount = useMemo(
    () => visibleCustomFields.filter((f) => Boolean(selectedIds[String(f.id)])).length,
    [selectedIds, visibleCustomFields]
  );
  const isManualValid = manualTitle.trim().length > 0;
  const isAddDisabled =
    mode === 'from_candidate_items'
      ? selectedCount === 0 || isCreating
      : !isManualValid || isCreatingManual;

  const isAllSelected = useMemo(
    () =>
      visibleCustomFields.length > 0 &&
      visibleCustomFields.every((f) => Boolean(selectedIds[String(f.id)])),
    [selectedIds, visibleCustomFields]
  );
  const isSomeSelected = useMemo(
    () => visibleCustomFields.some((f) => Boolean(selectedIds[String(f.id)])) && !isAllSelected,
    [selectedIds, visibleCustomFields, isAllSelected]
  );

  const handleToggleAll = (checked: boolean) => {
    if (!checked) {
      setSelectedIds({});
      return;
    }
    setSelectedIds(
      visibleCustomFields.reduce<Record<string, boolean>>((acc, f) => {
        acc[String(f.id)] = true;
        return acc;
      }, {})
    );
  };

  const handleToggleOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) => ({ ...prev, [String(id)]: checked }));
  };

  const handleClearSelections = () => {
    setSelectedIds({});
    setErrorMessage(null);
  };

  const handleBulkCreate = async () => {
    if (isAddDisabled) return;
    setErrorMessage(null);

    const ids = visibleCustomFields
      .filter((f) => Boolean(selectedIds[String(f.id)]))
      .map((f) => f.id);

    try {
      await bulkCreate({ customFieldIds: ids });
      showSuccessToast('追加しました');
      onAdded?.();
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '項目の追加に失敗しました');
    }
  };

  const handleManualCreate = async () => {
    if (isAddDisabled) return;
    setErrorMessage(null);

    const isSelectType =
      manualFormType === 'selectbox' || manualFormType === 'radio' || manualFormType === 'checkbox';
    const normalizedOptions = manualOptions.map((opt) => opt.trim()).filter(Boolean);

    const payload = {
      title: manualTitle.trim(),
      required: manualRequired,
      item_type: 'manual' as const,
      form_type: manualFormType,
      format_type: manualFormatType,
      ...(manualDescription.trim() ? { description: manualDescription.trim() } : {}),
      ...(manualPlaceholder.trim() ? { placeholder: manualPlaceholder.trim() } : {}),
      ...(manualFormType === 'text' && manualSuffix.trim() ? { suffix: manualSuffix.trim() } : {}),
      ...(manualMinLength.trim() ? { min_length: parseInt(manualMinLength, 10) } : {}),
      ...(manualMaxLength.trim() ? { max_length: parseInt(manualMaxLength, 10) } : {}),
      ...(isSelectType ? { options: normalizedOptions } : {}),
    };

    try {
      await createItem(payload);
      showSuccessToast('追加しました');
      onAdded?.();
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '項目の追加に失敗しました');
    }
  };

  const handleSubmit = async () => {
    if (mode === 'from_candidate_items') {
      await handleBulkCreate();
      return;
    }
    await handleManualCreate();
  };

  const segTabClassName = (active: boolean, side: 'left' | 'right') =>
    cn(
      'h-9 border border-solid border-border px-xs transition-colors',
      side === 'left' ? 'rounded-l-sm' : '-ml-px rounded-r-sm',
      active
        ? 'bg-primary_lighten text-button-md text-primary'
        : 'bg-white text-body-sm text-sub hover:bg-gray_darken'
    );

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="flex max-h-[85vh] min-h-0 w-[860px] flex-col overflow-hidden rounded-sm shadow-layer3!">
        <div className="flex items-center justify-between border-b border-solid border-border px-lg py-md">
          <DialogTitle className="text-heading-xs! text-main">質問項目の追加</DialogTitle>
          {mode === 'from_candidate_items' && (
            <Button
              type="button"
              color="tertiary"
              size="md"
              disabled={selectedCount === 0}
              onClick={handleClearSelections}
            >
              全ての選択を解除
            </Button>
          )}
        </div>

        <div className="hidden-scrollbar flex min-h-0 flex-1 flex-col gap-md overflow-y-auto px-lg py-md">
          <div className="flex items-center">
            <button
              type="button"
              className={segTabClassName(mode === 'from_candidate_items', 'left')}
              onClick={() => setMode('from_candidate_items')}
            >
              求職者の項目から追加
            </button>
            <button
              type="button"
              className={segTabClassName(mode === 'manual', 'right')}
              onClick={() => setMode('manual')}
            >
              手動で項目を追加
            </button>
          </div>

          {mode === 'from_candidate_items' ? (
            <AddItemCandidateItems
              customFields={visibleCustomFields}
              selectedIds={selectedIds}
              isAllSelected={isAllSelected}
              isSomeSelected={isSomeSelected}
              isLoading={isLoading}
              isError={isError}
              onToggleAll={handleToggleAll}
              onToggleOne={handleToggleOne}
            />
          ) : (
            <AddItemManual
              title={manualTitle}
              required={manualRequired}
              formType={manualFormType}
              description={manualDescription}
              placeholder={manualPlaceholder}
              suffix={manualSuffix}
              formatType={manualFormatType}
              options={manualOptions}
              minLength={manualMinLength}
              maxLength={manualMaxLength}
              onTitleChange={setManualTitle}
              onRequiredChange={setManualRequired}
              onFormTypeChange={(next) => {
                setManualFormType(next);
                if (next !== 'selectbox' && next !== 'radio' && next !== 'checkbox') {
                  setManualOptions([]);
                }
              }}
              onDescriptionChange={setManualDescription}
              onPlaceholderChange={setManualPlaceholder}
              onSuffixChange={setManualSuffix}
              onFormatTypeChange={setManualFormatType}
              onOptionsChange={setManualOptions}
              onMinLengthChange={setManualMinLength}
              onMaxLengthChange={setManualMaxLength}
            />
          )}

          {errorMessage && (
            <div className="rounded-sm bg-danger_lighten px-3 py-2">
              <p className="text-xs text-danger">{errorMessage}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-md border-t border-solid border-border px-lg py-md">
          <Button type="button" color="secondary" size="md" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            type="button"
            color="primary"
            size="md"
            disabled={isAddDisabled}
            onClick={handleSubmit}
          >
            {isCreating || isCreatingManual ? '追加中...' : '追加する'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
